import { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, View, StyleSheet, ScrollView, Pressable, Linking } from "react-native";
import { useGlobalSearchParams, router, usePathname, useSegments } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import { ShiftType } from "@/types/db/shifts";
import { supabase } from "@/lib/supabase-client";
import { expenseService } from "@/services/expenseService";
import { ExpenseType } from "@/types/db/expenses";
import { incidentService } from "@/services/incidentService";
import { IncidentType } from "@/types/db/incidents";
import { MaterialCommunityIcons, FontAwesome5 } from "@expo/vector-icons";
import { ModernCard, HeroSection, StatusBadge, TimeChip, PositionRosterTab } from "@/components/crew";
import Avatar from "@/components/Avatar";
import Modal from "@/components/Modal";
import { QRCodeModal } from "@/components/Modals/QRCodeModal";

const formatUserName = (user: any) => {
  if (!user) return "";
  const firstName = user.preferred_name || user.first_name || "";
  const lastName = user.last_name || "";
  return `${firstName} ${lastName}`.trim();
};

export default function SupervisedShiftDetailsPage() {
  // useGlobalSearchParams (not Local): nested [shift] routes do not hand Local the parent param -> stuck loading
  const params = useGlobalSearchParams<{ shift?: string }>();
  const shiftId = params.shift;
  const pathname = usePathname();
  const segments = useSegments();
  const { colors } = useTheme();
  const { displayNames, user } = useAuth();
  const shiftName = displayNames?.shifts?.name || "Shift";

  const getParentPath = () => {
    const pathParts = pathname.split("/");
    return pathParts.slice(0, -1).join("/");
  };

  const basePath = (segments as string[]).includes("crew") ? "/crew/supervising" : "/supervising";

  const handleStartSurvey = () => {
    router.navigate(`${basePath}/${shiftId}/survey` as any);
  };

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [shift, setShift] = useState<ShiftType | null>(null);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [expenses, setExpenses] = useState<ExpenseType[]>([]);
  const [expensesLoading, setExpensesLoading] = useState(true);
  const [incidents, setIncidents] = useState<IncidentType[]>([]);
  const [incidentsLoading, setIncidentsLoading] = useState(true);

  // Fetch shift
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!shiftId) return;
      setLoading(true);
      setErr(null);
      try {
        const { data: fnData, error: fnError } = await supabase.functions.invoke("get-shift", {
          body: { id: shiftId },
        });
        if (fnError) throw new Error(fnError.message || "Failed to fetch shift");
        const row = (fnData as ShiftType) ?? null;
        if (!alive) return;
        setShift(row);
        if (!row) setErr(`${displayNames?.shifts?.name || "Shift"} not found.`);
      } catch (e: any) {
        if (!alive) return;
        setErr(e?.message ?? `Failed to load ${displayNames?.shifts?.name?.toLowerCase() || "shift"}.`);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [shiftId, displayNames?.shifts?.name]);

  // Fetch expenses for this shift by current user
  useEffect(() => {
    if (!shiftId || !user?.id) return;
    let alive = true;
    (async () => {
      setExpensesLoading(true);
      try {
        const data = await expenseService.getExpensesByShiftAndUser(shiftId, user.id);
        if (alive) setExpenses(data);
      } catch (e) {
        console.error("Failed to load expenses:", e);
      } finally {
        if (alive) setExpensesLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [shiftId, user?.id]);

  // Fetch incidents for this shift by current user
  useEffect(() => {
    if (!shiftId || !user?.id) return;
    let alive = true;
    (async () => {
      setIncidentsLoading(true);
      try {
        const data = await incidentService.getIncidentsByShiftAndUser(shiftId, user.id);
        if (alive) setIncidents(data);
      } catch (e) {
        console.error("Failed to load incidents:", e);
      } finally {
        if (alive) setIncidentsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [shiftId, user?.id]);

  // Calculate shift status and times
  const shiftInfo = useMemo(() => {
    if (!shift?.start_time) return null;
    const now = new Date();
    const start = new Date(shift.start_time);
    const end = shift.end_time ? new Date(shift.end_time) : null;

    const duration = end ? Math.round(((end.getTime() - start.getTime()) / (1000 * 60 * 60)) * 10) / 10 : 0;

    let status: "upcoming" | "ready" | "active" | "completed" = "upcoming";
    if (end && now > end) status = "completed";
    else if (now > start) status = "active";
    else if (now > new Date(start.getTime() - 15 * 60 * 1000)) status = "ready";

    return {
      start,
      end,
      duration,
      status,
      date: start.toLocaleDateString([], {
        weekday: "long",
        month: "short",
        day: "numeric",
      }),
      startTime: start.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      endTime: end?.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  }, [shift?.start_time, shift?.end_time]);

  // Get staffing stats
  const staffingStats = useMemo(() => {
    const positions = shift?.shift_positions || [];
    let totalHeadcount = 0;
    let acceptedCount = 0;
    let pendingCount = 0;
    let declinedCount = 0;

    positions.forEach((pos) => {
      const headcount = (pos as any).headcount || 1;
      totalHeadcount += headcount;
      (pos.shift_users || []).forEach((su) => {
        if (su.status === "ACCEPTED") acceptedCount++;
        else if (su.status === "PENDING" || su.status === "INVITED") pendingCount++;
        else if (su.status === "DECLINED") declinedCount++;
      });
    });

    return { totalHeadcount, acceptedCount, pendingCount, declinedCount };
  }, [shift?.shift_positions]);

  // Get venue type
  const venueType = useMemo(() => {
    const shiftVenue = (shift?.meta_data as any)?.venue_type;
    if (shiftVenue && shiftVenue !== "Unspecified") return shiftVenue;
    const projectVenue = (shift?.project as any)?.meta_data?.venue_type;
    if (projectVenue && projectVenue !== "Unspecified") return projectVenue;
    return null;
  }, [shift?.meta_data, shift?.project]);

  // Check if QR code clock-in is enabled (shift or inherited from project)
  const hasQrCodeClockIn = useMemo(() => {
    const shiftOptions = (shift?.meta_data as any)?.clock_in_options;
    const projectOptions = (shift?.project as any)?.meta_data?.clock_in_options;
    return shiftOptions?.qr_code || (!shiftOptions && projectOptions?.qr_code);
  }, [shift]);

  // Check if timesheet clock-in is enabled (shift or inherited from project)
  const hasTimesheetClockIn = useMemo(() => {
    const shiftOptions = (shift?.meta_data as any)?.clock_in_options;
    const projectOptions = (shift?.project as any)?.meta_data?.clock_in_options;
    return shiftOptions?.timesheet || (!shiftOptions && projectOptions?.timesheet);
  }, [shift]);

  const getExpenseStatusVariant = (status?: string): "pending" | "accepted" | "declined" | "completed" => {
    switch (status) {
      case "APPROVED":
      case "PAID":
      case "REIMBURSED":
        return "completed";
      case "REJECTED":
        return "declined";
      default:
        return "pending";
    }
  };

  const getIncidentStatusVariant = (status?: string): "pending" | "accepted" | "declined" | "completed" => {
    switch (status) {
      case "RESOLVED":
        return "completed";
      case "ESCALATED":
        return "declined";
      case "UNDER REVIEW":
        return "accepted";
      default:
        return "pending";
    }
  };

  if (!shiftId || loading) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (err || !shift) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <MaterialCommunityIcons name="alert-circle-outline" size={48} color={colors.red.border} />
        <ThemedText style={{ marginTop: 12, fontSize: 16 }}>
          {err ?? `${displayNames?.shifts?.name || "Shift"} not found`}
        </ThemedText>
        <Button text="Go Back" icon="arrow-left" onPress={() => router.dismiss()} style={{ marginTop: 16 }} />
      </View>
    );
  }

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.background }]}
      contentContainerStyle={{ paddingBottom: 24 }}
      showsVerticalScrollIndicator={false}
    >
      {/* Hero Section */}
      <HeroSection style={styles.hero}>
        <View style={styles.heroContent}>
          <ThemedText style={styles.heroProject} numberOfLines={2}>
            {shift.name || `Untitled ${displayNames?.shifts?.name || "Shift"}`}
            {"  "}
            <ThemedText style={[styles.shiftId, { color: colors.placeholder }]}>
              {shift.id?.slice(0, 6).toUpperCase()}
            </ThemedText>
          </ThemedText>
          {(shift.project?.client?.name || shift.project?.name) && (
            <ThemedText style={styles.heroProjectSubtitle} numberOfLines={1}>
              {[shift.project?.client?.name, shift.project?.name].filter(Boolean).join(" · ")}
            </ThemedText>
          )}

          <View style={styles.heroDetailsRow}>
            <View style={styles.heroTimeRow}>
              <FontAwesome5 name="calendar" size={14} color="white" style={{ opacity: 0.9 }} />
              <ThemedText style={styles.heroTime}>{shiftInfo?.date}</ThemedText>
            </View>

            <View style={styles.heroTimeRow}>
              <FontAwesome5 name="clock" size={14} color="white" style={{ opacity: 0.9 }} />
              <ThemedText style={styles.heroTime}>{shiftInfo?.startTime}</ThemedText>
            </View>

            <View style={styles.heroTimeRow}>
              <MaterialCommunityIcons name="timelapse" size={14} color="white" style={{ opacity: 0.9 }} />
              <ThemedText style={styles.heroTime}>~{shiftInfo?.duration}h</ThemedText>
            </View>
          </View>

          {/* Status Badge */}
          <View style={styles.heroStatus}>
            <StatusBadge
              variant={
                shiftInfo?.status === "active" ? "active" : shiftInfo?.status === "completed" ? "completed" : "ready"
              }
              text={shiftInfo?.status?.toUpperCase() || "UNKNOWN"}
              icon={
                shiftInfo?.status === "active"
                  ? "clock-fast"
                  : shiftInfo?.status === "completed"
                    ? "check-circle"
                    : "clock-outline"
              }
            />
            {/* QR Code Button */}
            {hasQrCodeClockIn && (
              <Button
                icon="qrcode"
                text="Show QR"
                onPress={() => setQrModalVisible(true)}
                style={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderColor: "rgba(255,255,255,0.3)",
                  borderWidth: 1,
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  height: 32,
                  marginLeft: 8,
                }}
                textStyle={{ color: "white", fontSize: 12 }}
              />
            )}
          </View>
        </View>
      </HeroSection>

      {/* Staffing Overview Card */}
      <ModernCard style={styles.card}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="account-group" size={20} color={colors.text} />
          <ThemedText type="defaultSemiBold">Staffing Overview</ThemedText>
        </View>

        <View style={styles.statsGrid}>
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: colors.emerald?.text || "#10b981" }]}>
              {staffingStats.acceptedCount}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: colors.textSecondary }]}>Accepted</ThemedText>
          </View>
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: colors.amber?.text || "#f59e0b" }]}>
              {staffingStats.pendingCount}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: colors.textSecondary }]}>Pending</ThemedText>
          </View>
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: colors.red.text }]}>
              {staffingStats.declinedCount}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: colors.textSecondary }]}>Declined</ThemedText>
          </View>
          <View style={styles.statItem}>
            <ThemedText style={styles.statValue}>{staffingStats.totalHeadcount}</ThemedText>
            <ThemedText style={[styles.statLabel, { color: colors.textSecondary }]}>Needed</ThemedText>
          </View>
        </View>
      </ModernCard>

      {/* Positions & Crew Members */}
      {shift.shift_positions && shift.shift_positions.length > 0 && (
        <PositionRosterTab positions={shift.shift_positions} shiftId={shiftId!} style={styles.card} />
      )}

      {/* Shift Details Card */}
      <ModernCard style={styles.card}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="information-outline" size={20} color={colors.text} />
          <ThemedText type="defaultSemiBold">{displayNames?.shifts?.name || "Shift"} Details</ThemedText>
        </View>

        <View style={styles.detailsGrid}>
          {venueType && (
            <View style={styles.detailRow}>
              <ThemedText style={[styles.detailLabel, { color: colors.textSecondary }]}>Venue Type</ThemedText>
              <ThemedText style={styles.detailValue}>{venueType}</ThemedText>
            </View>
          )}

          {shift.location?.name && (
            <View style={styles.detailRow}>
              <ThemedText style={[styles.detailLabel, { color: colors.textSecondary }]}>Location</ThemedText>
              <ThemedText style={styles.detailValue}>{shift.location.name}</ThemedText>
            </View>
          )}

          {shift.meta_data?.description && (
            <View style={styles.detailRow}>
              <ThemedText style={[styles.detailLabel, { color: colors.textSecondary }]}>Description</ThemedText>
              <ThemedText style={styles.detailValue}>{shift.meta_data.description}</ThemedText>
            </View>
          )}
        </View>
      </ModernCard>

      {/* Onsite Contact */}
      {shift.onsite_contact_details && (
        <ModernCard style={styles.card}>
          <View style={styles.cardHeader}>
            <MaterialCommunityIcons name="account-tie" size={20} color={colors.text} />
            <ThemedText type="defaultSemiBold">Onsite Contact</ThemedText>
          </View>
          <View
            style={[styles.contactCard, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}
          >
            <View style={styles.contactRow}>
              <Pressable
                onPress={() => router.push(`${basePath}/${shiftId}/contact` as any)}
                style={({ pressed }) => [styles.contactHeader, pressed && { opacity: 0.7 }]}
              >
                <Avatar
                  firstName={shift.onsite_contact_details.name.split(" ")[0]}
                  lastName={shift.onsite_contact_details.name.split(" ").slice(1).join(" ")}
                  size={36}
                />
                <ThemedText style={styles.contactName}>{shift.onsite_contact_details.name}</ThemedText>
                <MaterialCommunityIcons name="chevron-right" size={18} color={colors.textSecondary} />
              </Pressable>
              {shift.onsite_contact_details.phone_number && (
                <View style={styles.contactActions}>
                  <Pressable
                    onPress={() => Linking.openURL(`tel:${shift.onsite_contact_details!.phone_number}`)}
                    style={({ pressed, hovered }) => [
                      styles.contactActionButton,
                      { backgroundColor: colors.emerald?.background, borderColor: colors.emerald?.border },
                      hovered && { opacity: 0.8 },
                      pressed && { opacity: 0.6 },
                    ]}
                  >
                    <MaterialCommunityIcons name="phone" size={18} color={colors.emerald?.text} />
                  </Pressable>
                  <Pressable
                    onPress={() => Linking.openURL(`sms:${shift.onsite_contact_details!.phone_number}`)}
                    style={({ pressed, hovered }) => [
                      styles.contactActionButton,
                      { backgroundColor: colors.violet?.background, borderColor: colors.violet?.border },
                      hovered && { opacity: 0.8 },
                      pressed && { opacity: 0.6 },
                    ]}
                  >
                    <MaterialCommunityIcons name="message-text" size={18} color={colors.violet?.text} />
                  </Pressable>
                </View>
              )}
            </View>
          </View>
        </ModernCard>
      )}

      {/* Other Supervisors */}
      {shift.shift_supervisors && shift.shift_supervisors.length > 0 && (
        <ModernCard style={styles.card}>
          <View style={styles.cardHeader}>
            <MaterialCommunityIcons name="account-tie" size={20} color={colors.text} />
            <ThemedText type="defaultSemiBold">Supervisors</ThemedText>
          </View>

          <View style={styles.crewList}>
            {shift.shift_supervisors.map((supervisor: any) => (
              <View key={supervisor.user_id} style={[styles.crewItem, { backgroundColor: colors.backgroundSecondary }]}>
                <View style={styles.crewInfo}>
                  <ThemedText style={styles.crewName}>
                    {formatUserName(supervisor.user) || "Unknown Supervisor"}
                  </ThemedText>
                  {supervisor.user?.email_address && (
                    <ThemedText style={[styles.crewEmail, { color: colors.textSecondary }]}>
                      {supervisor.user.email_address}
                    </ThemedText>
                  )}
                </View>
              </View>
            ))}
          </View>
        </ModernCard>
      )}

      {/* Survey Action */}
      <ModernCard
        style={[
          styles.card,
          (!hasTimesheetClockIn || shift.status === "COMPLETE" || staffingStats.acceptedCount === 0) && {
            opacity: 0.5,
          },
        ]}
      >
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons
            name={
              shift.status === "COMPLETE"
                ? "clipboard-check"
                : hasTimesheetClockIn
                  ? "clipboard-check-outline"
                  : "clipboard-off-outline"
            }
            size={20}
            color={
              shift.status === "COMPLETE"
                ? colors.emerald?.text
                : hasTimesheetClockIn
                  ? colors.text
                  : colors.textSecondary
            }
          />
          <ThemedText
            type="defaultSemiBold"
            style={(!hasTimesheetClockIn || shift.status === "COMPLETE") && { color: colors.textSecondary }}
          >
            Supervisor Survey
          </ThemedText>
          {shift.status === "COMPLETE" && <StatusBadge variant="completed" text="COMPLETE" size="small" />}
        </View>
        {shift.status === "COMPLETE" ? (
          <ThemedText style={[styles.surveyDescription, { color: colors.textSecondary, fontStyle: "italic" }]}>
            Survey has already been submitted for this {shiftName.toLowerCase()}.
          </ThemedText>
        ) : !hasTimesheetClockIn ? (
          <ThemedText style={[styles.surveyDescription, { color: colors.textSecondary, fontStyle: "italic" }]}>
            Timesheet clock-in is not enabled for this {shiftName.toLowerCase()}.
          </ThemedText>
        ) : staffingStats.acceptedCount === 0 ? (
          <ThemedText style={[styles.surveyDescription, { color: colors.textSecondary, fontStyle: "italic" }]}>
            No accepted crew members for this {shiftName.toLowerCase()}. Survey requires at least one accepted user.
          </ThemedText>
        ) : (
          <>
            <ThemedText style={[styles.surveyDescription, { color: colors.textSecondary }]}>
              Complete the end-of-{shiftName.toLowerCase()} survey to record attendance, standouts, and timesheet notes.
            </ThemedText>
            <Button
              text="Start Survey"
              icon="clipboard-edit-outline"
              iconPosition="left"
              onPress={handleStartSurvey}
              style={{ marginTop: 8, height: 48 }}
            />
          </>
        )}
      </ModernCard>

      {/* Expense Action */}
      <ModernCard style={styles.card}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="receipt" size={20} color={colors.text} />
          <ThemedText type="defaultSemiBold">Expenses</ThemedText>
          {expenses.length > 0 && (
            <View style={[styles.countBadge, { backgroundColor: colors.primary }]}>
              <ThemedText style={styles.countBadgeText}>{expenses.length}</ThemedText>
            </View>
          )}
        </View>

        {expensesLoading ? (
          <ActivityIndicator size="small" color={colors.primary} />
        ) : expenses.length > 0 ? (
          <View style={styles.expenseList}>
            {expenses.map((expense) => (
              <View key={expense.id} style={[styles.expenseItem, { backgroundColor: colors.backgroundSecondary }]}>
                <View style={styles.expenseInfo}>
                  <ThemedText style={styles.expenseName}>
                    {expense.meta_data?.expense_number ? `Expense #${expense.meta_data.expense_number}` : "Expense"}
                  </ThemedText>
                  <ThemedText style={[styles.expenseAmount, { color: colors.textSecondary }]}>
                    ${expense.meta_data?.amount?.toFixed(2) || "0.00"}
                    {expense.meta_data?.category && ` • ${expense.meta_data.category}`}
                  </ThemedText>
                </View>
                <StatusBadge
                  variant={getExpenseStatusVariant(expense.status)}
                  text={expense.status || "PENDING"}
                  size="small"
                />
              </View>
            ))}
          </View>
        ) : (
          <ThemedText style={[styles.surveyDescription, { color: colors.textSecondary }]}>
            No expenses submitted for this {shiftName.toLowerCase()} yet.
          </ThemedText>
        )}

        <Button
          text={expenses.length > 0 ? "Create Another" : "Create Expense"}
          icon="cash-plus"
          iconPosition="left"
          onPress={() => router.navigate(`${basePath}/${shiftId}/expense` as any)}
          style={{ marginTop: 12, height: 48 }}
        />
      </ModernCard>

      {/* Incident Action */}
      <ModernCard style={styles.card}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="alert-circle-outline" size={20} color={colors.text} />
          <ThemedText type="defaultSemiBold">Incidents</ThemedText>
          {incidents.length > 0 && (
            <View style={[styles.countBadge, { backgroundColor: colors.amber?.border || colors.primary }]}>
              <ThemedText style={styles.countBadgeText}>{incidents.length}</ThemedText>
            </View>
          )}
        </View>

        {incidentsLoading ? (
          <ActivityIndicator size="small" color={colors.primary} />
        ) : incidents.length > 0 ? (
          <View style={styles.expenseList}>
            {incidents.map((incident) => (
              <View key={incident.id} style={[styles.expenseItem, { backgroundColor: colors.backgroundSecondary }]}>
                <View style={styles.expenseInfo}>
                  <ThemedText style={styles.expenseName}>
                    {incident.meta_data?.incident_number || incident.name || "Incident"}
                  </ThemedText>
                  <ThemedText style={[styles.expenseAmount, { color: colors.textSecondary }]}>
                    {incident.name}
                  </ThemedText>
                </View>
                <StatusBadge
                  variant={getIncidentStatusVariant(incident.status)}
                  text={incident.status || "PENDING REVIEW"}
                  size="small"
                />
              </View>
            ))}
          </View>
        ) : (
          <ThemedText style={[styles.surveyDescription, { color: colors.textSecondary }]}>
            No incidents reported for this {shiftName.toLowerCase()} yet.
          </ThemedText>
        )}

        <Button
          text={incidents.length > 0 ? "Report Another" : "Report Incident"}
          icon="alert-plus"
          iconPosition="left"
          onPress={() => router.navigate(`${basePath}/${shiftId}/incident` as any)}
          style={{ marginTop: 12, height: 48 }}
        />
      </ModernCard>

      {/* QR Code Modal */}
      {qrModalVisible && shiftId && (
        <Modal onUnmount={() => setQrModalVisible(false)}>
          <QRCodeModal shiftId={shiftId} onClose={() => setQrModalVisible(false)} />
        </Modal>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  cardHeader: {
    gap: 8,
    flexDirection: "row",
    marginBottom: 16,
  },

  // Hero
  hero: {
    paddingTop: 60,
    paddingBottom: 32,
  },
  heroContent: {
    gap: 16,
  },
  heroProject: {
    fontSize: 28,
    fontWeight: "800",
    color: "white",
  },
  shiftId: {
    fontSize: 14,
    fontWeight: "400",
  },
  heroProjectSubtitle: {
    fontSize: 16,
    fontWeight: "500",
    color: "white",
    opacity: 0.85,
    marginTop: -8,
  },
  heroDetailsRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16,
  },
  heroTimeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  heroTime: {
    fontSize: 15,
    color: "white",
    opacity: 0.9,
    fontWeight: "500",
  },
  heroStatus: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },

  // Cards
  card: {
    marginHorizontal: 16,
    marginTop: 16,
  },

  // Stats Grid
  statsGrid: {
    flexDirection: "row",
    justifyContent: "space-around",
  },
  statItem: {
    alignItems: "center",
  },
  statValue: {
    fontSize: 24,
    fontWeight: "700",
  },
  statLabel: {
    fontSize: 12,
    marginTop: 4,
  },

  // Position Meta
  positionMeta: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 16,
  },

  // Crew List
  crewList: {
    gap: 8,
  },
  crewItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 12,
    borderRadius: 8,
  },
  crewInfo: {
    flex: 1,
    marginRight: 12,
  },
  crewName: {
    fontSize: 14,
    fontWeight: "600",
  },
  crewEmail: {
    fontSize: 12,
    marginTop: 2,
  },
  noCrewText: {
    fontSize: 14,
    fontStyle: "italic",
    textAlign: "center",
    paddingVertical: 12,
  },

  // Details Grid
  detailsGrid: {
    gap: 12,
  },
  detailRow: {
    gap: 4,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: "500",
  },
  detailValue: {
    fontSize: 14,
  },

  // Survey
  surveyDescription: {
    fontSize: 14,
    lineHeight: 20,
  },

  // Expenses
  expenseList: {
    gap: 8,
    marginBottom: 4,
  },
  expenseItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 12,
    borderRadius: 8,
  },
  expenseInfo: {
    flex: 1,
    marginRight: 12,
  },
  expenseName: {
    fontSize: 14,
    fontWeight: "600",
  },
  expenseAmount: {
    fontSize: 12,
    marginTop: 2,
  },
  countBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    marginLeft: "auto",
  },
  countBadgeText: {
    fontSize: 12,
    fontWeight: "700",
    color: "white",
  },

  // Onsite Contact
  contactCard: {
    padding: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  contactRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  contactHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  contactName: {
    fontSize: 14,
    fontWeight: "500",
    flex: 1,
  },
  contactActions: {
    flexDirection: "row",
    gap: 8,
  },
  contactActionButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
  },
});
